import { DEVICE_STATUSES, LEVEL_LABEL, SIM_STATUSES } from '@app/shared';
import { REASON_REQUIRED_STATUSES } from '@app/shared';
import type { InventoryEventDto, UnitDto, UnitKind, UnitStatus } from '@app/shared';
import type { Option } from '../../components/form';

/** The reference dashboard shows the status `Free` as "Available". */
export const statusLabel = (status: UnitStatus): string =>
  status === 'Free' ? 'Available' : status;

/** Statuses a unit of this kind can take; Fraud and Lost/Stolen are SIM only. */
export const statusOptions = (kind: UnitKind): Option[] =>
  (kind === 'device' ? DEVICE_STATUSES : SIM_STATUSES).map((s) => ({
    value: s,
    label: statusLabel(s),
  }));

/**
 * Statuses that need a change reason. Mirrors the server rule (an assumption, see
 * docs/technical/inventory.md); the server enforces it and this only drives the form.
 */
export { REASON_REQUIRED_STATUSES };

/** SIM and IMEI as the list shows them: a paired unit also shows its partner's identifier. */
export const simOf = (u: UnitDto): string =>
  u.sim ?? (u.paired?.kind === 'sim' ? u.paired.identifier : '');
export const imeiOf = (u: UnitDto): string =>
  u.imei ?? (u.paired?.kind === 'device' ? u.paired.identifier : '');

/** "Store Type" is the owner's level in the agent hierarchy. */
export const storeType = (u: UnitDto): string => (u.owner ? LEVEL_LABEL[u.owner.level] : '');

export const productLabel = (m: { make: string; model: string }): string =>
  `${m.make} ${m.model}`.trim();

const FIELD_LABEL: Record<string, string> = {
  model: 'Product Name',
  plan: 'Plan',
  zip: 'Zipcode',
  puk2: 'PUK 2',
  poNumber: 'PO Number',
  reservedForPortin: 'Reserve for portin',
  taggedWith: 'Tagged with',
  // Update Inventory (PC391).
  msid: 'MSID',
  box: 'Box',
  notes: 'Notes',
  uiccid: 'UICCID',
  byod: 'BYOD',
  wholesalePriceCents: 'Wholesale Price',
  sellingPriceCents: 'Selling Price',
  activationFeeCents: 'Activation Fee',
  retailPriceCents: 'Device Retail Price',
  acpCoPayCents: 'ACP Co-Pay Amount',
  acpReimbursementCents: 'ACP Device Reimbursement Amount',
};
const isCents = (key: string) => key.endsWith('Cents');

const show = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'none';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};
const money = (value: unknown): string =>
  typeof value === 'number' ? `$${(value / 100).toFixed(2)}` : 'none';
const owner = (side: Record<string, unknown> | null): string =>
  side && typeof side.owner === 'string' ? side.owner : 'unassigned stock';

/** One readable line for an entry of a unit's history. */
export function describeEvent(e: InventoryEventDto): string {
  const from = e.from ?? {};
  const to = e.to ?? {};
  switch (e.type) {
    case 'added':
      return `Added to inventory (${owner(e.to)})`;
    case 'reassigned':
      return `Reassigned from ${owner(e.from)} to ${owner(e.to)}`;
    case 'assigned':
      return `Assigned to customer ${show(to.customerId)}${to.mdn ? `, telephone number ${show(to.mdn)}` : ''}`;
    case 'cleared':
      // A bulk clear of stock no customer holds has no customer to name.
      return `${from.customerId ? `Taken back from customer ${show(from.customerId)}` : 'Cleared'}; now ${statusLabel(to.status as UnitStatus)}${
        e.reason ? `. Reason: ${e.reason}` : ''
      }`;
    case 'statusChanged':
      return `Status changed from ${statusLabel(from.status as UnitStatus)} to ${statusLabel(to.status as UnitStatus)}${
        e.reason ? `. Reason: ${e.reason}` : ''
      }`;
    case 'updated': {
      const changes = Object.keys(FIELD_LABEL)
        .filter((key) => key in to)
        .map((key) =>
          isCents(key)
            ? `${FIELD_LABEL[key]}: ${money(from[key])} to ${money(to[key])}`
            : `${FIELD_LABEL[key]}: ${show(from[key])} to ${show(to[key])}`,
        );
      return changes.length ? `Updated ${changes.join('; ')}` : 'Updated';
    }
  }
}
